import { type ReactNode, useRef, useState } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface TruncatedTextTooltipProps {
  children: ReactNode;
  className?: string;
}

export function TruncatedTextTooltip({
  children,
  className
}: TruncatedTextTooltipProps) {
  const [isTruncated, setIsTruncated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const evaluateTruncation = () => {
    if (!textRef.current) {
      setIsTruncated(false);
      return false;
    }

    const truncated = textRef.current.scrollWidth > textRef.current.clientWidth;
    setIsTruncated(truncated);
    return truncated;
  };

  const handleMouseEnter = () => {
    setIsOpen(evaluateTruncation());
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleFocus = () => {
    setIsOpen(evaluateTruncation());
  };

  const handleBlur = () => {
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsOpen(false);
      return;
    }

    if (textRef.current) {
      setIsOpen(evaluateTruncation());
    }
  };

  return (
    <Tooltip
      onOpenChange={handleOpenChange}
      open={isOpen && isTruncated}
    >
      <TooltipTrigger
        asChild
        onBlur={handleBlur}
        onFocus={handleFocus}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={cn('truncate', className)}
          ref={textRef}
        >
          {children}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-md">
        <p>{children}</p>
      </TooltipContent>
    </Tooltip>
  );
}
